import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Menu, X, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const languages = [
  { code: "en", label: "English", flag: "EN" },
  { code: "de", label: "Deutsch", flag: "DE" },
  { code: "ar", label: "\u0627\u0644\u0639\u0631\u0628\u064a\u0629", flag: "AR" },
];

const navLinks = ["home", "about", "products", "gallery", "contact"];

export default function Navbar() {
  const { t, language, setLanguage, isRTL } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
    setMobileOpen(false);
  };

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-neutral-100"
      style={{ direction: isRTL ? "rtl" : "ltr" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <button
           data-testid="navbar-logo"
           onClick={() => scrollTo("home")}
           className="font-heading tracking-tight flex flex-col items-start"
          >
          <span className="text-2xl font-bold" style={{ color: "#8B0000" }}>
           Al-Kasbah
          </span>
          <span className="text-xs font-normal text-neutral-400 hidden sm:block">
           {t("nav.tagline")}
          </span>
          </button> 

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link}
                data-testid={`nav-${link}`}
                onClick={() => scrollTo(link)}
                className="text-sm font-medium text-neutral-600 hover:text-[#8B0000] transition-colors tracking-wide uppercase"
              >
                {t(`nav.${link}`)}
              </button>
            ))}

            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="language-switcher"
                  className="gap-2 border-neutral-200"
                >
                  <Globe className="h-4 w-4" />
                  {languages.find((l) => l.code === language)?.flag}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={isRTL ? "start" : "end"}>
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    data-testid={`lang-${lang.code}`}
                    onClick={() => setLanguage(lang.code)}
                    className={language === lang.code ? "bg-neutral-100 font-semibold" : ""}
                  >
                    <span className="mr-2 font-mono text-xs">{lang.flag}</span>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-language-switcher">
                  <Globe className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={isRTL ? "start" : "end"}>
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    data-testid={`mobile-lang-${lang.code}`}
                    onClick={() => setLanguage(lang.code)}
                  >
                    <span className="mr-2 font-mono text-xs">{lang.flag}</span>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle">
                  {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </Button>
              </SheetTrigger>
              <SheetContent side={isRTL ? "left" : "right"} className="w-64 bg-white">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex flex-col gap-6 mt-8">
                  {navLinks.map((link) => (
                    <button
                      key={link}
                      data-testid={`mobile-nav-${link}`}
                      onClick={() => scrollTo(link)}
                      className="text-base font-medium text-neutral-700 hover:text-[#8B0000] transition-colors text-left uppercase tracking-wide"
                    >
                      {t(`nav.${link}`)}
                    </button>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
